<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="jps-cs-selector"
      :no-header="props.noHeader"
      :no-closer="props.noCloser"
      :no-footer="props.noFooter"
      dialog-width="100vw"
      dialog-height="100vh"
    >
      <div class="jps-modal-content" v-if="ss">
        <SearchBox :ss="ss" />
        <SearchCount :ss="ss" />
        <UiDataPanel
          nolink
          selectable
          type="csearch"
          :data="cross"
          @click="selectCs(cross)"
        />
        <UiDataPanel
          v-if="props.allowGallery"
          nolink
          selectable
          type="csearch"
          :data="gallery"
          @click="selectCs(gallery)"
        />
        <div v-if="ss.searched">
          <UiDataPanel
            v-for="cs in ss.state.result.list"
            :key="cs.id"
            type="csearch"
            :data="cs"
            selectable
            tablink
            nolink
            @click="selectCs(cs)"
          />
          <SearchInfiniteLoading :ss="ss" />
        </div>
      </div>
      <template #footer-extra v-if="multiple">
        <div class="modal-footer-selected">
          <UiCsearchSpan
            class="jps-tag jps-dark jps-tag-ricon"
            v-for="id in selectedValue"
            :key="id"
            :id="id"
          >
            <template #default="{ csearch }">
              {{ ls(csearch.name) }}
              <BaseIcon
                class="jps-icon-button is-size-6"
                name="mdi/close-circle"
                @click="removeFromArray(selectedValue, csearch.id)"
              />
            </template>
          </UiCsearchSpan>
        </div>
      </template>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'

const { ls } = useI18n()
const { isOrg, getOrgId } = useUserStore()
const { searchCustomSearch } = useCustomSearch()
const dataStore = useDataStore()

interface Props {
  multiple: boolean
  selected: string[]
  allowGallery: boolean
  allowAllTypes: boolean
  noHeader?: boolean
  noCloser?: boolean
  noFooter?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  multiple: false,
  selected: () => [],
  allowGallery: false,
  allowAllTypes: false,
  noHeader: true,
  noCloser: true,
  noFooter: false,
})

const modal = ref<InstanceType<typeof BaseModal>>()
const ss: Ref<SearchStore<CustomSearchDefinition>> = ref()
const openAwait = async () => {
  if (props.selected) selectedValue.value = clone(props.selected)
  ss.value = useSearch({ keywordFields: ['keywords'] }, searchCustomSearch, {
    configSyncRoute: false,
    configFreeze: false,
    queryRewriter: (q: any) => {
      q.permission = 'M'
      if (props.allowAllTypes) {
        if (isOrg) {
          q.subquery = [
            { c: 'OR', q: { filter: { type: ['+NORMAL', '+DB'] } } },
            {
              c: 'OR',
              q: {
                filter: {
                  type: ['NOTE'],
                  'permission.ownerOrg': [getOrgId],
                },
              },
            },
          ]
        } else {
          q.subquery = [{ c: 'AND', q: { filter: { type: ['+NORMAL', '+DB'] } } }]
        }
      } else {
        q.filter = { type: ['NORMAL'] }
      }
      return q
    },
  })

  await ss.value.execute()
  nextTick(() => {
    ss.value.requestKeywordFocus()
  })
  return await modal.value.openAwait<CustomSearchDefinition | string[]>()
}
defineExpose({ openAwait })

const cross: CustomSearchDefinition = systemCrossSearchCs()
const gallery: CustomSearchDefinition = systemGallerySearchCs()
const selectedValue: Ref<string[]> = ref([])

const selectCs = (cs: CustomSearchDefinition) => {
  if (props.multiple) {
    if (!selectedValue.value.includes(cs.id)) {
      dataStore.put('csearch', cs.id, cs)
      selectedValue.value.push(cs.id)
    } else {
      removeFromArray(selectedValue.value, cs.id)
    }
  } else {
    modal.value.resolve(cs)
  }
}

const confirm = () => {
  modal.value.resolve(selectedValue.value)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-cs-selector {
  .jps-search-box {
    margin-bottom: 0.25rem;
  }

  .jps-data-panel {
    margin-bottom: 0.375rem;
  }

  .modal-footer-selected {
    padding: 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;

    .jps-tag {
      margin: 0.125rem 0;
    }

    max-height: 116px;
    overflow-y: auto;
  }
}
</style>
